import React, { useState, useEffect } from 'react';
import {
  AIAgent,
  AgentConfiguration,
  AutomationLevel,
  RiskLevel,
  DecisionType,
  AgentAlert
} from '../../../types/aiAgent';
import { aiAgentService } from '../../../services/aiAgentService';

interface AIAgentConfigurationProps {
  agent: AIAgent;
  onConfigurationUpdate?: (agentId: string, config: AgentConfiguration) => void;
  onAgentUpdate?: (agentId: string, updates: Partial<AIAgent>) => void;
  onClose?: () => void;
}

export const AIAgentConfiguration: React.FC<AIAgentConfigurationProps> = ({
  agent,
  onConfigurationUpdate,
  onAgentUpdate,
  onClose
}) => {
  const [config, setConfig] = useState<AgentConfiguration>(agent.configuration);
  const [automationLevel, setAutomationLevel] = useState<AutomationLevel>(agent.automationLevel);
  const [activeTab, setActiveTab] = useState<'automation' | 'approval' | 'performance' | 'constraints' | 'preferences'>('automation');
  const [saving, setSaving] = useState(false);
  const [_alerts, setAlerts] = useState<AgentAlert[]>([]);

  useEffect(() => {
    loadAgentAlerts();
  }, [agent.id]);

  const loadAgentAlerts = async () => {
    // TODO: Load alerts from API
    setAlerts([]);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await aiAgentService.updateAgent(agent.id, {
        configuration: config,
        automationLevel: automationLevel
      });
      onConfigurationUpdate?.(agent.id, config);
      onAgentUpdate?.(agent.id, { automationLevel });
    } catch (error) {
      console.error('Failed to save configuration:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateApprovalThreshold = (decisionType: DecisionType, field: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      approvalThresholds: {
        ...prev.approvalThresholds,
        [decisionType]: {
          ...(prev.approvalThresholds?.[decisionType] || {
            requiresApproval: false,
            confidenceThreshold: 0.8,
            impactThreshold: 0.1
          }),
          [field]: value
        }
      }
    }));
  };

  const updatePerformanceTarget = (field: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      performanceTargets: {
        ...(prev.performanceTargets || {
          successRate: 0.8,
          improvementRate: 0.1,
          responseTime: 30
        }),
        [field]: value
      }
    }));
  };

  const updateConstraint = (field: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      constraints: {
        ...(prev.constraints || {
          maxConcurrentTasks: 5,
          maxBudgetPerTest: 10000,
          allowedTestDuration: { min: 7, max: 30 },
          restrictedElements: []
        }),
        [field]: value
      }
    }));
  };

  const updatePreference = (field: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      preferences: {
        ...(prev.preferences || {
          preferredTestTypes: ['content', 'cta'],
          defaultConfidenceLevel: 95,
          communicationStyle: 'summary'
        }),
        [field]: value
      }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-surface rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">
              Configure {agent.name}
            </h3>
            <p className="text-sm text-content-secondary mt-1">
              Automation level, approval workflows, and performance settings
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-content-subtle hover:text-content-secondary"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-line">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'automation', label: 'Automation Level', icon: '⚙️' },
              { id: 'approval', label: 'Approval Workflows', icon: '✅' },
              { id: 'performance', label: 'Performance Targets', icon: '📊' },
              { id: 'constraints', label: 'Constraints', icon: '🚫' },
              { id: 'preferences', label: 'Preferences', icon: '⭐' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
                  }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'automation' && (
            <AutomationLevelTab
              config={config}
              automationLevel={automationLevel}
              onUpdateConfig={(updates) => setConfig(prev => ({ ...prev, ...updates }))}
              onUpdateAutomationLevel={setAutomationLevel}
            />
          )}

          {activeTab === 'approval' && (
            <ApprovalWorkflowTab
              config={config}
              onUpdateThreshold={updateApprovalThreshold}
            />
          )}

          {activeTab === 'performance' && (
            <PerformanceTargetsTab
              config={config}
              onUpdateTarget={updatePerformanceTarget}
            />
          )}

          {activeTab === 'constraints' && (
            <ConstraintsTab
              config={config}
              onUpdateConstraint={updateConstraint}
            />
          )}

          {activeTab === 'preferences' && (
            <PreferencesTab
              config={config}
              onUpdatePreference={updatePreference}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-line-strong rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Automation Level Configuration Tab
const AutomationLevelTab: React.FC<{
  config: AgentConfiguration;
  automationLevel: AutomationLevel;
  onUpdateConfig: (updates: Partial<AgentConfiguration>) => void;
  onUpdateAutomationLevel: (level: AutomationLevel) => void;
}> = ({ config, automationLevel, onUpdateConfig, onUpdateAutomationLevel }) => {
  const automationLevels: { value: AutomationLevel; label: string; description: string }[] = [
    {
      value: 'manual',
      label: 'Manual',
      description: 'Agent provides recommendations but requires human approval for all actions'
    },
    {
      value: 'assisted',
      label: 'Assisted',
      description: 'Agent can perform low-risk actions automatically, requires approval for medium/high risk'
    },
    {
      value: 'supervised',
      label: 'Supervised',
      description: 'Agent operates autonomously with periodic human oversight and approval for critical decisions'
    },
    {
      value: 'autonomous',
      label: 'Autonomous',
      description: 'Agent operates independently with minimal human intervention, only critical failures require attention'
    }
  ];

  const riskLevels: { value: RiskLevel; label: string; description: string }[] = [
    {
      value: 'low',
      label: 'Low Risk Tolerance',
      description: 'Conservative approach, requires approval for most decisions'
    },
    {
      value: 'medium',
      label: 'Medium Risk Tolerance',
      description: 'Balanced approach, approvals required for significant changes'
    },
    {
      value: 'high',
      label: 'High Risk Tolerance',
      description: 'Aggressive approach, minimal approvals required'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-content mb-4">Automation Level</h4>
        <div className="space-y-3">
          {automationLevels.map(level => (
            <label key={level.value} className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="automationLevel"
                value={level.value}
                checked={automationLevel === level.value}
                onChange={(e) => onUpdateAutomationLevel(e.target.value as AutomationLevel)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-content">{level.label}</div>
                <div className="text-sm text-content-secondary">{level.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h4 className="text-lg font-medium text-content mb-4">Risk Tolerance</h4>
        <div className="space-y-3">
          {riskLevels.map(risk => (
            <label key={risk.value} className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="riskTolerance"
                value={risk.value}
                checked={config.riskTolerance === risk.value}
                onChange={(e) => onUpdateConfig({ riskTolerance: e.target.value as RiskLevel })}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-content">{risk.label}</div>
                <div className="text-sm text-content-secondary">{risk.description}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

// Approval Workflow Configuration Tab
const ApprovalWorkflowTab: React.FC<{
  config: AgentConfiguration;
  onUpdateThreshold: (decisionType: DecisionType, field: string, value: any) => void;
}> = ({ config, onUpdateThreshold }) => {
  const decisionTypes: { type: DecisionType; label: string; description: string }[] = [
    { type: 'test_creation', label: 'Test Creation', description: 'Creating new A/B tests' },
    { type: 'variant_generation', label: 'Variant Generation', description: 'Generating test variants' },
    { type: 'winner_implementation', label: 'Winner Implementation', description: 'Implementing winning variations' },
    { type: 'campaign_optimization', label: 'Campaign Optimization', description: 'Optimizing campaigns' },
    { type: 'audience_targeting', label: 'Audience Targeting', description: 'Modifying audience segments' },
    { type: 'statistical_analysis', label: 'Statistical Analysis', description: 'Performing statistical analysis' },
    { type: 'content_modification', label: 'Content Modification', description: 'Modifying content elements' },
    { type: 'resource_allocation', label: 'Resource Allocation', description: 'Allocating budget and resources' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-content mb-4">Decision Approval Thresholds</h4>
        <p className="text-sm text-content-secondary mb-6">
          Configure when human approval is required for different types of decisions
        </p>
      </div>

      <div className="space-y-6">
        {decisionTypes.map(decision => {
          const threshold = (config.approvalThresholds && config.approvalThresholds[decision.type]) || {
            requiresApproval: false,
            confidenceThreshold: 0.8,
            impactThreshold: 0.1
          };
          return (
            <div key={decision.type} className="border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h5 className="font-medium text-content">{decision.label}</h5>
                  <p className="text-sm text-content-secondary">{decision.description}</p>
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={threshold.requiresApproval}
                    onChange={(e) => onUpdateThreshold(decision.type, 'requiresApproval', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Requires Approval</span>
                </label>
              </div>

              {threshold.requiresApproval && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confidence Threshold
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={threshold.confidenceThreshold}
                      onChange={(e) => onUpdateThreshold(decision.type, 'confidenceThreshold', parseFloat(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-sm text-content-secondary mt-1">
                      {(threshold.confidenceThreshold * 100).toFixed(0)}% confidence required
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Impact Threshold
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={threshold.impactThreshold}
                      onChange={(e) => onUpdateThreshold(decision.type, 'impactThreshold', parseFloat(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-sm text-content-secondary mt-1">
                      {(threshold.impactThreshold * 100).toFixed(1)}% impact threshold
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Performance Targets Tab
const PerformanceTargetsTab: React.FC<{
  config: AgentConfiguration;
  onUpdateTarget: (field: string, value: number) => void;
}> = ({ config, onUpdateTarget }) => {
  const targets = config.performanceTargets || {
    successRate: 0.8,
    improvementRate: 0.1,
    responseTime: 30
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-content mb-4">Performance Targets</h4>
        <p className="text-sm text-content-secondary mb-6">
          Set performance expectations for the agent
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Success Rate Target
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={targets.successRate}
              onChange={(e) => onUpdateTarget('successRate', parseFloat(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm font-medium text-content w-16">
              {(targets.successRate * 100).toFixed(0)}%
            </span>
          </div>
          <p className="text-sm text-content-secondary mt-1">
            Minimum percentage of successful task completions
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Improvement Rate Target
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={targets.improvementRate}
              onChange={(e) => onUpdateTarget('improvementRate', parseFloat(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm font-medium text-content w-16">
              {(targets.improvementRate * 100).toFixed(1)}%
            </span>
          </div>
          <p className="text-sm text-content-secondary mt-1">
            Expected performance improvement from agent actions
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Response Time Target (seconds)
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="30"
              max="1800"
              step="30"
              value={targets.responseTime}
              onChange={(e) => onUpdateTarget('responseTime', parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm font-medium text-content w-16">
              {targets.responseTime}s
            </span>
          </div>
          <p className="text-sm text-content-secondary mt-1">
            Maximum time for agent to respond to tasks
          </p>
        </div>
      </div>
    </div>
  );
};

// Constraints Tab
const ConstraintsTab: React.FC<{
  config: AgentConfiguration;
  onUpdateConstraint: (field: string, value: any) => void;
}> = ({ config, onUpdateConstraint }) => {
  const constraints = config.constraints || {
    maxConcurrentTasks: 5,
    maxBudgetPerTest: 10000,
    allowedTestDuration: { min: 7, max: 30 },
    restrictedElements: []
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-content mb-4">Agent Constraints</h4>
        <p className="text-sm text-content-secondary mb-6">
          Set operational limits for the agent
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Maximum Concurrent Tasks
          </label>
          <input
            type="number"
            min="1"
            max="20"
            value={constraints.maxConcurrentTasks}
            onChange={(e) => onUpdateConstraint('maxConcurrentTasks', parseInt(e.target.value))}
            className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-content-secondary mt-1">
            Maximum number of tasks the agent can handle simultaneously
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Maximum Budget Per Test ($)
          </label>
          <input
            type="number"
            min="100"
            max="100000"
            step="100"
            value={constraints.maxBudgetPerTest}
            onChange={(e) => onUpdateConstraint('maxBudgetPerTest', parseInt(e.target.value))}
            className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-sm text-content-secondary mt-1">
            Maximum budget the agent can allocate to a single test
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Test Duration Limits (days)
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-content-secondary mb-1">Minimum</label>
              <input
                type="number"
                min="1"
                max="365"
                value={constraints.allowedTestDuration.min}
                onChange={(e) => onUpdateConstraint('allowedTestDuration', {
                  ...constraints.allowedTestDuration,
                  min: parseInt(e.target.value)
                })}
                className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-content-secondary mb-1">Maximum</label>
              <input
                type="number"
                min="1"
                max="365"
                value={constraints.allowedTestDuration.max}
                onChange={(e) => onUpdateConstraint('allowedTestDuration', {
                  ...constraints.allowedTestDuration,
                  max: parseInt(e.target.value)
                })}
                className="w-full border border-line-strong rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <p className="text-sm text-content-secondary mt-1">
            Allowed range for test duration
          </p>
        </div>
      </div>
    </div>
  );
};

// Preferences Tab
const PreferencesTab: React.FC<{
  config: AgentConfiguration;
  onUpdatePreference: (field: string, value: any) => void;
}> = ({ config, onUpdatePreference }) => {
  const testTypes = ['content', 'cta', 'headline', 'image', 'layout', 'color', 'form'];
  const communicationStyles = [
    { value: 'detailed', label: 'Detailed', description: 'Comprehensive explanations and data' },
    { value: 'summary', label: 'Summary', description: 'Key points and conclusions' },
    { value: 'minimal', label: 'Minimal', description: 'Essential information only' }
  ];

  const preferences = config.preferences || {
    preferredTestTypes: ['content', 'cta'],
    defaultConfidenceLevel: 95,
    communicationStyle: 'summary'
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-content mb-4">Agent Preferences</h4>
        <p className="text-sm text-content-secondary mb-6">
          Configure agent behavior preferences
        </p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Preferred Test Types
          </label>
          <div className="grid grid-cols-3 gap-2">
            {testTypes.map(type => (
              <label key={type} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={preferences.preferredTestTypes.includes(type)}
                  onChange={(e) => {
                    const current = preferences.preferredTestTypes;
                    const updated = e.target.checked
                      ? [...current, type]
                      : current.filter(t => t !== type);
                    onUpdatePreference('preferredTestTypes', updated);
                  }}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{type}</span>
              </label>
            ))}
          </div>
          <p className="text-sm text-content-secondary mt-1">
            Types of tests the agent should prioritize
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Default Confidence Level
          </label>
          <div className="flex items-center space-x-4">
            <input
              type="range"
              min="80"
              max="99"
              step="1"
              value={preferences.defaultConfidenceLevel}
              onChange={(e) => onUpdatePreference('defaultConfidenceLevel', parseInt(e.target.value))}
              className="flex-1"
            />
            <span className="text-sm font-medium text-content w-12">
              {preferences.defaultConfidenceLevel}%
            </span>
          </div>
          <p className="text-sm text-content-secondary mt-1">
            Default statistical confidence level for tests
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Communication Style
          </label>
          <div className="space-y-2">
            {communicationStyles.map(style => (
              <label key={style.value} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="radio"
                  name="communicationStyle"
                  value={style.value}
                  checked={preferences.communicationStyle === style.value}
                  onChange={(e) => onUpdatePreference('communicationStyle', e.target.value)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-content">{style.label}</div>
                  <div className="text-sm text-content-secondary">{style.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};